import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TeamBadge } from "@/components/TeamBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Users, Plus, ArrowLeft, Copy, LogOut, ChevronRight, Send, Crown, Share2, MessageCircle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function PrivateLeagues({ leagues = [], currentLeagueId = null, open: openProp, onOpenChange: onOpenChangeProp }) {
  const { t } = useI18n();
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const [groups, setGroups] = useState([]);
  const [view, setView] = useState("list");
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("table");
  const [messages, setMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState({ has_unread: false, groups: {} });
  // Versus / H2H
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [h2hLeague, setH2hLeague] = useState(currentLeagueId || "");
  const [compare, setCompare] = useState(null);
  const [vsToken, setVsToken] = useState(null);

  const loadUnread = useCallback(async () => {
    try { const { data } = await api.get("/groups/unread"); setUnread(data); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    loadUnread();
    const iv = setInterval(loadUnread, 30000);
    return () => clearInterval(iv);
  }, [loadUnread]);

  const loadGroups = async () => {
    const { data } = await api.get("/groups");
    setGroups(data.groups);
  };

  const onOpenChange = (o) => {
    if (onOpenChangeProp) onOpenChangeProp(o); else setOpenState(o);
    if (o) { setView("list"); loadGroups(); loadUnread(); }
    else loadUnread();
  };

  const openGroup = async (id) => {
    const { data } = await api.get(`/groups/${id}/leaderboard`);
    setDetail(data);
    setDetailTab("table");
    setMessages([]);
    setCompare(null);
    const me = data.leaderboard.find((r) => r.is_me);
    const other = data.leaderboard.find((r) => !r.is_me);
    setAId(me?.user_id || data.leaderboard[0]?.user_id || "");
    setBId(other?.user_id || data.leaderboard[0]?.user_id || "");
    setH2hLeague(currentLeagueId || leagues[0]?.id || "");
    setView("detail");
  };

  const loadMessages = async (id) => {
    const { data } = await api.get(`/groups/${id}/messages`);
    setMessages(data.messages);
  };

  const markRead = async (id) => {
    try { await api.post(`/groups/${id}/read`); loadUnread(); } catch { /* noop */ }
  };

  const openChat = async () => {
    setDetailTab("chat");
    if (detail) { await loadMessages(detail.group.id); markRead(detail.group.id); }
  };

  const sendMessage = async () => {
    if (!chatText.trim() || !detail) return;
    const text = chatText;
    setChatText("");
    try { await api.post(`/groups/${detail.group.id}/messages`, { text }); await loadMessages(detail.group.id); markRead(detail.group.id); }
    catch { toast.error(t("pl.error")); }
  };

  const loadCompare = useCallback(async () => {
    setVsToken(null);
    if (!detail || !aId || !bId || !h2hLeague || aId === bId) { setCompare(null); return; }
    try {
      const { data } = await api.get(`/groups/${detail.group.id}/compare`, { params: { league_id: h2hLeague, a: aId, b: bId } });
      setCompare(data);
    } catch { setCompare(null); }
  }, [detail, aId, bId, h2hLeague]);

  useEffect(() => { if (detailTab === "h2h") loadCompare(); }, [detailTab, loadCompare]);

  const shareVersus = async () => {
    if (!detail) return;
    try {
      const { data } = await api.get(`/groups/${detail.group.id}/versus-link`, { params: { league_id: h2hLeague, a: aId, b: bId, base: BACKEND } });
      setVsToken(data.token);
      toast.success(t("share.title"));
    } catch { toast.error(t("pl.error")); }
  };
  const vsPageUrl = vsToken ? `${BACKEND}/api/share/versus/${vsToken}` : "";
  const vsImgUrl = vsToken ? `${BACKEND}/api/share/versus/card/${vsToken}.png` : "";
  const vsCaption = compare ? t("vshare.caption", { a: compare.a.name, b: compare.b.name, league: compare.league.name }) : "";
  const vsWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(vsCaption + " " + vsPageUrl)}`, "_blank");
  const vsX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(vsCaption)}&url=${encodeURIComponent(vsPageUrl)}`, "_blank");
  const vsCopy = async () => { try { await navigator.clipboard.writeText(vsPageUrl); toast.success(t("share.copied")); } catch { toast.error(t("pl.error")); } };

  const createGroup = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/groups", { name }); setName(""); toast.success(t("pl.created")); await loadGroups(); await openGroup(data.id); }
    catch (e) { toast.error(e.response?.data?.detail || t("pl.error")); }
    finally { setBusy(false); }
  };

  const joinGroup = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/groups/join", { code }); setCode(""); toast.success(t("pl.joined")); await loadGroups(); await openGroup(data.id); }
    catch (e) { toast.error(e.response?.data?.detail || t("pl.error")); }
    finally { setBusy(false); }
  };

  const leaveGroup = async () => {
    if (!detail) return;
    await api.delete(`/groups/${detail.group.id}/leave`);
    toast.success(t("pl.left"));
    setView("list"); setDetail(null); loadGroups(); loadUnread();
  };

  const copyCode = async (c) => {
    try { await navigator.clipboard.writeText(c); toast.success(t("pl.copied")); }
    catch { toast.error(t("pl.error")); }
  };

  const members = detail?.leaderboard || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="private-leagues-btn" className="relative">
          <Users size={16} className="me-2" /> <span className="hidden sm:inline">{t("pl.button")}</span>
          {unread.has_unread && <span data-testid="unread-dot" className="absolute top-1 end-1 w-2 h-2 bg-primary rounded-full animate-pulse" />}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-2 border-foreground rounded-none max-w-md">
        {view === "list" ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl uppercase">{t("pl.title")}</DialogTitle>
              <DialogDescription className="font-data text-xs">{t("pl.desc")}</DialogDescription>
            </DialogHeader>

            <div className="space-y-5">
              <div>
                <p className="overline text-primary mb-2">{t("pl.myLeagues")}</p>
                <div className="border-2 border-border" data-testid="pl-groups-list">
                  {groups.length === 0 && <p className="text-muted-foreground text-sm py-4 text-center">{t("pl.none")}</p>}
                  {groups.map((g) => (
                    <button key={g.id} onClick={() => openGroup(g.id)} data-testid={`pl-group-${g.id}`}
                      className="w-full flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-b-0 hover:bg-primary/10 transition-colors text-start">
                      <span className="flex-1 min-w-0">
                        <span className="font-semibold flex items-center gap-2 truncate">
                          {g.name}
                          {unread.groups[g.id] && <span data-testid={`pl-group-unread-${g.id}`} className="w-2 h-2 bg-primary rounded-full shrink-0" />}
                        </span>
                        <span className="font-data text-[11px] text-muted-foreground">{g.code} · {g.member_count} {t("pl.members")}</span>
                      </span>
                      <ChevronRight size={16} className="text-muted-foreground rtl:rotate-180" />
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div>
                  <p className="overline text-muted-foreground mb-2">{t("pl.create")}</p>
                  <div className="flex gap-2">
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("pl.createName")} data-testid="pl-create-name" className="h-10" />
                    <Button onClick={createGroup} disabled={busy} data-testid="pl-create-btn" className="shrink-0"><Plus size={16} className="me-1" /> {t("pl.createBtn")}</Button>
                  </div>
                </div>
                <div>
                  <p className="overline text-muted-foreground mb-2">{t("pl.join")}</p>
                  <div className="flex gap-2">
                    <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="GAFFER-XXXX" data-testid="pl-join-code" className="h-10 font-data uppercase" />
                    <Button onClick={joinGroup} disabled={busy} variant="outline" data-testid="pl-join-btn" className="shrink-0">{t("pl.joinBtn")}</Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <button onClick={() => setView("list")} data-testid="pl-back-btn" className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors mb-1 w-fit">
                <ArrowLeft size={14} className="rtl:rotate-180" /> {t("pl.back")}
              </button>
              <DialogTitle className="font-display text-3xl uppercase">{detail?.group.name}</DialogTitle>
              <DialogDescription className="font-data text-xs">{detail?.group.member_count} {t("pl.members")}</DialogDescription>
            </DialogHeader>

            <div className="flex items-center justify-between border-2 border-border px-3 py-2">
              <div>
                <p className="overline text-muted-foreground">{t("pl.invite")}</p>
                <p className="font-data font-bold text-lg text-primary" data-testid="pl-invite-code">{detail?.group.code}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => copyCode(detail.group.code)} data-testid="pl-copy-code"><Copy size={14} className="me-1.5" /> {t("pl.copy")}</Button>
            </div>

            {/* Table / Banter / Versus toggle */}
            <div className="grid grid-cols-3 border-2 border-border">
              {[["table", t("pl.tab.table")], ["chat", t("pl.tab.chat")], ["h2h", t("pl.tab.h2h")]].map(([k, label], i) => (
                <button key={k} data-testid={`pl-tab-${k}`}
                  onClick={() => k === "chat" ? openChat() : setDetailTab(k)}
                  className={`py-2 font-data text-xs uppercase tracking-wider transition-colors ${i > 0 ? "border-s-2 border-border" : ""} ${detailTab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-primary/10"} ${k === "chat" ? "relative" : ""}`}>
                  {label}
                  {k === "chat" && detail && unread.groups[detail.group.id] && detailTab !== "chat" && <span className="absolute top-1.5 end-2 w-1.5 h-1.5 bg-primary rounded-full" />}
                </button>
              ))}
            </div>

            {detailTab === "table" && (
              <div className="max-h-[42vh] overflow-y-auto border-2 border-foreground" data-testid="pl-leaderboard">
                {members.map((r) => (
                  <div key={r.user_id} data-testid={`pl-row-${r.rank}`} className={`flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-b-0 ${r.is_me ? "bg-primary/15" : "even:bg-white/[0.03]"}`}>
                    <span className="font-display text-xl w-7 text-center text-muted-foreground">{r.rank}</span>
                    <span className="font-medium flex-1 truncate flex items-center gap-1.5">
                      {r.rank === 1 && r.points > 0 && <Crown size={15} className="text-[hsl(var(--stadium-yellow))] shrink-0" data-testid={`champion-crown-${r.rank}`} title={t("champ.title")} />}
                      <span className="truncate">{r.name}</span>
                      {r.is_me && <span className="text-primary text-xs font-data shrink-0">· {t("lb.you")}</span>}
                    </span>
                    <span className="text-xs text-muted-foreground font-data">{r.predicted} {t("lb.preds")}</span>
                    <span className="font-data font-bold text-lg text-primary w-10 text-end">{r.points}</span>
                  </div>
                ))}
              </div>
            )}

            {detailTab === "chat" && (
              <div className="border-2 border-foreground" data-testid="pl-chat">
                <div className="max-h-[34vh] overflow-y-auto p-3">
                  <div className="flex flex-col justify-end min-h-[128px] gap-2.5">
                    {messages.length === 0 && <p className="text-muted-foreground text-sm text-center py-6 font-data">{t("chat.empty")}</p>}
                    {messages.map((m) => (
                      <div key={m.id} data-testid="chat-msg" className={`flex flex-col ${m.is_me ? "items-end" : "items-start"}`}>
                        <span className="font-data text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">{m.is_me ? t("lb.you") : m.name}</span>
                        <span className={`text-sm px-3 py-1.5 border-2 max-w-[85%] break-words ${m.is_me ? "bg-primary/15 border-primary/40" : "bg-white/[0.03] border-border"}`}>{m.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 p-2 border-t-2 border-border">
                  <Input value={chatText} onChange={(e) => setChatText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendMessage(); }} placeholder={t("chat.placeholder")} data-testid="chat-input" className="h-10" maxLength={500} />
                  <Button onClick={sendMessage} data-testid="chat-send-btn" className="shrink-0"><Send size={15} /></Button>
                </div>
              </div>
            )}

            {detailTab === "h2h" && (
              <div data-testid="pl-h2h">
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <select value={aId} onChange={(e) => setAId(e.target.value)} data-testid="h2h-a"
                    className="bg-background border-2 border-border h-10 px-2 font-data text-xs uppercase tracking-wide">
                    {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                  </select>
                  <select value={bId} onChange={(e) => setBId(e.target.value)} data-testid="h2h-b"
                    className="bg-background border-2 border-border h-10 px-2 font-data text-xs uppercase tracking-wide">
                    {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                  </select>
                </div>
                <select value={h2hLeague} onChange={(e) => setH2hLeague(e.target.value)} data-testid="h2h-league"
                  className="w-full bg-background border-2 border-border h-10 px-2 font-data text-xs uppercase tracking-wide mb-3">
                  {leagues.map((l) => <option key={l.id} value={l.id}>{l.emoji} {l.name}</option>)}
                </select>

                {aId === bId ? (
                  <p className="text-muted-foreground text-sm text-center py-6 font-data" data-testid="h2h-same">{t("h2h.same")}</p>
                ) : compare ? (
                  <>
                    <H2HTable compare={compare} t={t} />
                    <div className="mt-3 border-2 border-border p-2">
                      {!vsToken ? (
                        <Button onClick={shareVersus} variant="outline" size="sm" className="w-full" data-testid="versus-share-btn">
                          <Share2 size={15} className="me-2" /> {t("vshare.button")}
                        </Button>
                      ) : (
                        <div className="grid grid-cols-2 gap-2" data-testid="versus-share-actions">
                          <Button onClick={vsWhatsApp} size="sm" data-testid="versus-whatsapp-btn"
                            className="bg-[hsl(145_63%_42%)] text-white border-2 border-transparent hover:brightness-110">
                            <MessageCircle size={15} className="me-1.5" /> {t("share.whatsapp")}
                          </Button>
                          <Button onClick={vsX} size="sm" data-testid="versus-x-btn"
                            className="bg-foreground text-background border-2 border-transparent hover:brightness-110">
                            {t("share.x")}
                          </Button>
                          <Button onClick={vsCopy} size="sm" variant="outline" data-testid="versus-copy-btn">
                            <Copy size={14} className="me-1.5" /> {t("share.copy")}
                          </Button>
                          <Button onClick={() => window.open(vsImgUrl, "_blank")} size="sm" variant="outline" data-testid="versus-image-btn">
                            <ImageIcon size={14} className="me-1.5" /> {t("share.download")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-6 font-data">…</p>
                )}
              </div>
            )}

            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={leaveGroup} data-testid="pl-leave-btn" className="text-muted-foreground hover:text-destructive"><LogOut size={14} className="me-1.5" /> {t("pl.leave")}</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function H2HTable({ compare, t }) {
  const posB = Object.fromEntries(compare.b.table.map((r) => [r.team_id, r.position]));
  const rows = [...compare.a.table].sort((x, y) => x.position - y.position);
  const diffs = rows.filter((r) => posB[r.team_id] !== r.position).length;
  const total = rows.length;

  return (
    <div data-testid="h2h-result">
      <div className="flex items-center justify-between mb-2">
        <p className="overline text-primary" data-testid="h2h-diffs">
          {diffs === 0 ? t("h2h.agree") : t("h2h.diffs", { n: diffs, t: total, count: diffs })}
        </p>
      </div>
      <div className="border-2 border-foreground max-h-[38vh] overflow-y-auto">
        <div className="grid grid-cols-[3rem_1fr_3rem] items-center border-b-2 border-border bg-foreground text-background font-data text-[10px] uppercase tracking-wider">
          <span className="text-center py-1.5 truncate px-1">{compare.a.name}</span>
          <span className="text-center py-1.5">Club</span>
          <span className="text-center py-1.5 truncate px-1">{compare.b.name}</span>
        </div>
        {rows.map((r, i) => {
          const pb = posB[r.team_id];
          const differ = pb !== r.position;
          return (
            <div key={r.team_id} data-testid={`h2h-row-${r.short}`}
              className={`grid grid-cols-[3rem_1fr_3rem] items-center border-b border-border last:border-b-0 ${differ ? "bg-primary/10" : "even:bg-white/[0.03]"}`}>
              <span className={`font-data font-bold text-center py-2 ${differ ? "text-primary" : "text-muted-foreground"}`}>{String(r.position).padStart(2, "0")}</span>
              <span className="flex items-center gap-2 py-2 min-w-0">
                <TeamBadge team={r} size={22} />
                <span className="font-semibold text-sm truncate">{r.name}</span>
              </span>
              <span className={`font-data font-bold text-center py-2 ${differ ? "text-primary" : "text-muted-foreground"}`}>{String(pb).padStart(2, "0")}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
